import { useState, useEffect, useRef } from 'react';
import './ToolbarFilterDropdown.css';

/**
 * Custom listbox for toolbar filters — same hover/selection styling as Employee Master
 * (native select option lists cannot be styled on Windows).
 */
export default function ToolbarFilterDropdown({
  id,
  labelText,
  ariaLabel,
  value,
  onChange,
  placeholder,
  options,
  menuKey,
  openMenu,
  setOpenMenu,
  fieldClass = '',
  wide = false,
}) {
  const open = openMenu === menuKey;
  const rootRef = useRef(null);
  const listRef = useRef(null);
  const triggerRef = useRef(null);
  const [highlight, setHighlight] = useState(0);

  const active = !!value;

  useEffect(() => {
    if (!open) return;
    const i = options.findIndex(o => o.value === value);
    setHighlight(i >= 0 ? i : 0);
  }, [open, value, options]);

  useEffect(() => {
    if (!open) return;
    const onDoc = (e) => {
      if (rootRef.current && !rootRef.current.contains(e.target)) setOpenMenu(null);
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [open, setOpenMenu]);

  useEffect(() => {
    if (open && listRef.current) listRef.current.focus({ preventScroll: true });
  }, [open]);

  useEffect(() => {
    if (!open || !listRef.current) return;
    const el = listRef.current.querySelector(`[data-idx="${highlight}"]`);
    el?.scrollIntoView({ block: 'nearest' });
  }, [open, highlight]);

  const display = (options.find(o => o.value === value) || { label: placeholder }).label;

  const close = () => setOpenMenu(null);

  const pick = (i) => {
    const opt = options[i];
    if (!opt) return;
    onChange(opt.value);
    close();
    triggerRef.current?.focus({ preventScroll: true });
  };

  const toggle = () => setOpenMenu(open ? null : menuKey);

  const onTriggerKeyDown = (e) => {
    if (e.key === 'Escape' && open) {
      e.preventDefault();
      close();
      return;
    }
    if (!open && (e.key === 'ArrowDown' || e.key === 'ArrowUp' || e.key === 'Enter' || e.key === ' ')) {
      e.preventDefault();
      setOpenMenu(menuKey);
    }
  };

  const onListKeyDown = (e) => {
    if (e.key === 'Escape') {
      e.preventDefault();
      close();
      triggerRef.current?.focus({ preventScroll: true });
      return;
    }
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setHighlight(h => Math.min(h + 1, options.length - 1));
      return;
    }
    if (e.key === 'ArrowUp') {
      e.preventDefault();
      setHighlight(h => Math.max(h - 1, 0));
      return;
    }
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      pick(highlight);
    }
  };

  return (
    <div
      ref={rootRef}
      className={`tfd-field${active ? ' tfd-field--active' : ''}${wide ? ' tfd-field--wide' : ''}${fieldClass}`}
    >
      <label htmlFor={id} className="tfd-label-sr" id={`${id}-label`}>
        {labelText}
      </label>
      <div className={`tfd-shell tfd-shell--dropdown${open ? ' tfd-shell--open' : ''}`}>
        <button
          ref={triggerRef}
          type="button"
          id={id}
          className="tfd-trigger"
          aria-label={ariaLabel}
          aria-haspopup="listbox"
          aria-expanded={open}
          aria-controls={`${id}-listbox`}
          onClick={e => {
            e.stopPropagation();
            toggle();
          }}
          onKeyDown={onTriggerKeyDown}
        >
          <span className="tfd-value">{display}</span>
        </button>
        {open && (
          <ul
            ref={listRef}
            id={`${id}-listbox`}
            role="listbox"
            tabIndex={-1}
            className="tfd-panel"
            aria-labelledby={`${id}-label`}
            onKeyDown={onListKeyDown}
          >
            {options.map((opt, i) => (
              <li
                key={opt.value === '' ? '__all' : String(opt.value)}
                role="option"
                aria-selected={value === opt.value}
                data-idx={i}
                className={`tfd-option${value === opt.value ? ' tfd-option--selected' : ''}${i === highlight ? ' tfd-option--highlight' : ''}`}
                onMouseEnter={() => setHighlight(i)}
                onPointerDown={e => {
                  e.preventDefault();
                  pick(i);
                }}
              >
                {opt.label}
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}
